import React, { ReactElement } from 'react';
import classNames from 'classnames/bind';
// (!) New user registration disabled (by the https://searate.atlassian.net/browse/IMPACT-2774)
// import Link from 'next/link';
import useWindowSize from 'utils/hooks/useWindowSize';
import { parseContent } from 'sections/Resources/FreightForwarder/helpers/textParser';
import styles from 'sections/Resources/FreightForwarder/landing/components/SectionInstructions/SectionInstructions.module.scss';

const cn = classNames.bind(styles);

type CountryContent = {
  title: string;
  paragraphs?: string[];
  list?: {
    title: string;
    items: string[];
  };
  summary?: string;
};

type SectionInstructionsProps = {
  country: string;
  countryContent?: CountryContent[] | null;
}

const SectionInstructions = ({ country, countryContent }: SectionInstructionsProps): ReactElement => {
  const { width } = useWindowSize();

  const isCountryWithContent = Boolean(countryContent);

  const renderImage = (sectionName: 'start' | 'steps' | 'become') => {
    if (sectionName === 'start') {
      if (width >= 1025) {
        return (
          <img src='/images/pages/Resources/FreightForwarder/landings/section-image-01_desktop.webp' alt='membership' />
        );
      } else if (width >= 768 && width <= 1024) {
        return (
          <img src='/images/pages/Resources/FreightForwarder/landings/section-image-01_tablet.webp' alt='membership' />
        );
      } else if (width <= 767) {
        return (
          <img src='/images/pages/Resources/FreightForwarder/landings/section-image-01_mobile.webp' alt='membership' />
        );
      }
    } else if (sectionName === 'steps') {
      if (width >= 1025) {
        return (
          <img src='/images/pages/Resources/FreightForwarder/landings/section-image-02_desktop.webp' alt='steps' />
        );
      } else if (width >= 768 && width <= 1024) {
        return (
          <img src='/images/pages/Resources/FreightForwarder/landings/section-image-02_tablet.webp' alt='steps' />
        );
      } else if (width <= 767) {
        return (
          <img src='/images/pages/Resources/FreightForwarder/landings/section-image-02_mobile.webp' alt='steps' />
        );
      }
    } else {
      if (width >= 1025) {
        return (
          <img src='/images/pages/Resources/FreightForwarder/landings/section-image-03_desktop.webp' alt='become' />
        );
      } else if (width >= 768 && width <= 1024) {
        return (
          <img src='/images/pages/Resources/FreightForwarder/landings/section-image-03_tablet.webp' alt='become' />
        );
      } else if (width <= 767) {
        return (
          <img src='/images/pages/Resources/FreightForwarder/landings/section-image-03_mobile.webp' alt='become' />
        );
      }
    }
  };

  return (
    <section className={cn('instructions', { 'with-content': isCountryWithContent })}>
      <div className={cn('container')}>
        <div className={cn('instructions__content')}>
          {countryContent ? (
            <>
              {countryContent.map(({ title, paragraphs, list, summary }, idx) => {
                return (
                  <React.Fragment key={idx}>
                    {title === ''
                      ?
                      <></>
                      :
                      <h2 className={cn('instructions__title')}>
                        {title}
                      </h2>
                    }
                    <div>
                      {paragraphs
                        ?
                        paragraphs.map((paragraph, idx) => (
                          <p
                            className={cn('instructions__paragraph')}
                            key={idx}
                          >
                            {parseContent(paragraph, cn, 'instructions')}
                          </p>
                        ))
                        :
                        null
                      }
                    </div>
                    {list ? (
                      <>
                        {list.title && <p className={cn('instructions__list-title')}>{list.title}</p>}
                        <ul className={cn('instructions__list')}>
                          {list.items.map((item, idx) => (
                            <li
                              className={cn('instructions__list-item')}
                              key={idx}
                            >
                              {parseContent(item, cn, 'instructions')}
                            </li>
                          ))}
                        </ul>
                      </>
                    ) : null}
                    {summary
                      ?
                      <p
                        key={idx}
                        className={cn('instructions__summary')}
                      >
                        {parseContent(summary, cn, 'instructions')}
                      </p>
                      :
                      null
                    }
                  </React.Fragment>
                )
              })}
            </>
          ) : (
            <>
              <div className={cn('instructions__section', 'start')}>
                <div className={cn('instructions__image-box', 'start')}>
                  {renderImage('start')}
                </div>
                <div className={cn('instructions__description-box', 'start')}>
                  <h3 className={cn('instructions__title')}>
                    Start your Digital Freight Alliance membership
                  </h3>
                  <div className={cn('instructions__paragraphs')}>
                    <p className={cn('instructions__paragraph')}>
                      for free to enhance your business and capabilities. Access a network of established freight
                      partners in India and across the world via our Members Directory- essential for navigating
                      the complex regulatory landscape and enjoy Logistics Explorer our free real-time freight
                      calculator.
                      Quote your rates to thousands of customers at any time and get online bookings for FCL, LCL, Air,
                      Road, Rail transport, and more.
                    </p>
                    <p className={cn('instructions__paragraph')}>
                      DFA also offers exert guidance for understanding customs regulations and operational best
                      practices through your personalised account and sales managers.
                    </p>
                  </div>
                </div>
              </div>

              <div className={cn('instructions__section', 'steps')}>
                <div className={cn('instructions__description-box', 'steps')}>
                  <h3 className={cn('instructions__title')}>
                    Join the Digital Freight Alliance (DFA) in four easy steps:
                  </h3>
                  <div className={cn('instructions__paragraphs')}>
                    <ol className={cn('instructions__list')}>
                      <li className={cn('instructions__list-item')}>
                        Choose Membership Tier: Select either Standard or Premium based on your needs,
                        each offering benefits like digital tools and trade finance.
                      </li>
                      <li className={cn('instructions__list-item')}>
                        Application: Complete the online form on the DFA website, providing basic business
                        details and your preferred membership.
                      </li>
                      <li className={cn('instructions__list-item')}>
                        Submit KYC: Fill out a Know Your Customer form to verify your business identity.
                      </li>
                      <li className={cn('instructions__list-item')}>
                        Payment and Activation: Make any necessary payments and activate your membership
                        to begin using DFA resources.
                      </li>
                    </ol>
                    <p className={cn('instructions__paragraph')}>
                      Each step is streamlined to quickly integrate your business into DFA’s global network.
                    </p>
                    {/* (!) New user registration disabled (by the https://searate.atlassian.net/browse/IMPACT-2774) */}
                    {/* <Link className={cn('instructions__button-join')} href="/membership">
                      Become a Member today
                    </Link> */}
                  </div>
                </div>
                <div className={cn('instructions__image-box', 'steps')}>
                  {renderImage('steps')}
                </div>
              </div>

              <div className={cn('instructions__section', 'become')}>
                <div className={cn('instructions__image-box', 'become')}>
                  {renderImage('become')}
                </div>
                <div className={cn('instructions__description-box', 'become')}>
                  <h3 className={cn('instructions__title')}>
                    How to Become a Freight Forwarder in {country}
                  </h3>
                  <div className={cn('instructions__paragraphs')}>
                    <p className={cn('instructions__paragraph', 'become')}>
                      Getting a freight forwarder license can be complex. Generally, the process includes
                      the following 5 steps:
                    </p>
                    <ol className={cn('instructions__list')}>
                      <li className={cn('instructions__list-item')}>
                        Research Legal Requirements: Investigate the specific regulations and requirements for
                        freight forwarders in {country}
                      </li>
                      <li className={cn('instructions__list-item')}>
                        Meet Licensing Criteria: Fulfill any necessary conditions for obtaining a license, which
                        may include industry experience, passing relevant exams, and securing appropriate
                        insurance coverage.
                      </li>
                      <li className={cn('instructions__list-item')}>
                        Submit Application: Apply for a license through the appropriate government department,
                        providing all required documents and paying any associated fees.
                      </li>
                      <li className={cn('instructions__list-item')}>
                        Processing Time: Allow time for your application to be processed and reviewed, which can vary
                        widely from a few weeks to several months depending on the location and the backlog of
                        applications.
                      </li>
                      <li className={cn('instructions__list-item')}>
                        License Issuance: Once approved, you will receive a license to operate as a freight forwarder,
                        enabling you to offer services like customs clearance, cargo transportation, and warehousing
                        for international freight.
                      </li>
                    </ol>
                  </div>
                </div>
              </div>
            </>
          )
          }

        </div>
      </div>
    </section>
  );
};

export default SectionInstructions;
